import { useState, useEffect, useMemo, useCallback } from 'react';
import { Toaster, toast } from 'react-hot-toast';
import { getInventory, searchInventory, createInventory, updateInventory, deleteInventory, getApiBaseUrl, downloadInventoryPhotosZip } from '../utils/api';
import { roundRupees } from '../utils/money';
import { formatModelCode } from '../utils/modelCategoryRanges';
import InventoryModal from './InventoryModal';
import InventoryToolbar from './InventoryToolbar';

const getStockStatusLabel = (currentStock) => {
  if (currentStock === 0) return 'OUT OF STOCK';
  if (currentStock <= 5) return 'LOW STOCK';
  return 'IN STOCK';
};

const InventoryGalleryPage = () => {
  const [inventory, setInventory] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedStockFilter, setSelectedStockFilter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItemForDetail, setSelectedItemForDetail] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [photoDownloadCategory, setPhotoDownloadCategory] = useState(null);
  const fetchInventory = useCallback(async () => {
    try {
      setLoading(true);
      let data;
      if (searchQuery.trim()) {
        data = await searchInventory(searchQuery);
      } else {
        data = await getInventory();
      }
      setInventory(data);
    } catch (error) {
      toast.error(error.message || 'Failed to load inventory');
      console.error('Error fetching inventory:', error);
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  const fetchCategories = useCallback(async () => {
    try {
      const baseUrl = getApiBaseUrl();
      const response = await fetch(`${baseUrl}/api/inventory/public/categories`);
      if (!response.ok) throw new Error('Failed to fetch categories');
      const cats = await response.json();
      setCategories(cats);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchInventory();
    }, 150);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, fetchInventory]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Prevent background scrolling when item details panel is open; Escape to close
  useEffect(() => {
    if (selectedItemForDetail) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }

    const onKeyDown = (event) => {
      if (event.key === 'Escape' && selectedItemForDetail) {
        setSelectedItemForDetail(null);
      }
    };
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.classList.remove('overflow-hidden');
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [selectedItemForDetail]);

  const filteredInventory = useMemo(() => {
    let filtered = inventory;

    // Apply category filter
    if (selectedCategory) {
      filtered = filtered.filter(item => item.size === selectedCategory);
    }

    // Apply stock level filter
    if (selectedStockFilter) {
      filtered = filtered.filter(item => {
        const stockPercentage = item.initialStock > 0 ? (item.currentStock / item.initialStock) * 100 : 0;

        switch (selectedStockFilter) {
          case 'zero':
            return item.currentStock === 0;
          case 'low':
            return item.currentStock > 0 && item.currentStock <= 5;
          case 'medium':
            return stockPercentage >= 40 && stockPercentage <= 60 && item.currentStock > 5;
          case 'full':
            return stockPercentage > 60;
          default:
            return true;
        }
      });
    }

    return filtered.sort((a, b) => (a.modelNumber ?? 0) - (b.modelNumber ?? 0));
  }, [inventory, selectedCategory, selectedStockFilter]);

  const handleSave = async (id, data) => {
    try {
      let result;
      if (id) {
        result = await updateInventory(id, data);
        toast.success('Inventory item updated successfully!');
      } else {
        result = await createInventory(data);
        toast.success('Inventory item created successfully!');
      }
      // Modal closes itself after create + photo upload; refresh happens on close
      return result;
    } catch (error) {
      toast.error(error.message || 'Failed to save inventory item');
      throw error;
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
    fetchInventory();
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete || deleteLoading) return;
    setDeleteLoading(true);
    const deletedItem = itemToDelete;
    setInventory(prev => prev.filter(item => item._id !== deletedItem._id));
    setShowDeleteConfirm(false);
    setItemToDelete(null);

    try {
      await deleteInventory(deletedItem._id);
      toast.success('Inventory item deleted successfully!');
    } catch (error) {
      toast.error(error.message || 'Failed to delete inventory item');
      setInventory(prev => [...prev, deletedItem].sort((a, b) => a.modelName.localeCompare(b.modelName)));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setItemToDelete(null);
  };

  const handleDownloadPhotos = async (category) => {
    if (photoDownloadCategory) return;
    setPhotoDownloadCategory(category);
    try {
      await downloadInventoryPhotosZip(category);
      toast.success(
        category === 'all'
          ? 'All idol photos downloaded'
          : `${category.charAt(0).toUpperCase() + category.slice(1)} photos downloaded`
      );
    } catch (error) {
      toast.error(error.message || 'Failed to download photos');
    } finally {
      setPhotoDownloadCategory(null);
    }
  };

  const photoDownloadButtons = [
    { id: 'all', label: 'All' },
    { id: 'small', label: 'Small' },
    { id: 'medium', label: 'Medium' },
    { id: 'large', label: 'Large' },
    { id: 'pen', label: 'Pen' },
  ];

  return (
    <div className="p-6">
      <Toaster position="top-right" />

      <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">
            Inventory Gallery
          </h1>
          <p className="text-gray-600">Visual inventory management with photo gallery</p>
        </div>

        <div
          className="shrink-0 border border-gray-200 rounded-lg bg-gray-50 px-3 py-2"
          title="ZIP with model code on each photo; file name matches the stamped code"
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-gray-700 shrink-0">Download photos</span>
            {photoDownloadButtons.map((btn) => {
              const isBusy = photoDownloadCategory === btn.id;
              const anyBusy = Boolean(photoDownloadCategory);
              return (
                <button
                  key={btn.id}
                  type="button"
                  onClick={() => handleDownloadPhotos(btn.id)}
                  disabled={anyBusy}
                  className="admin-btn admin-btn-neutral px-2.5 py-1 text-sm rounded disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isBusy ? 'Preparing…' : btn.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <InventoryToolbar
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        categories={categories}
        selectedCategory={selectedCategory}
        onCategoryChange={setSelectedCategory}
        selectedStockFilter={selectedStockFilter}
        onStockFilterChange={setSelectedStockFilter}
        collapsibleStockFilters
        onAddNew={() => {
          setSelectedItem(null);
          setIsModalOpen(true);
        }}
        resultCount={loading ? null : filteredInventory.length}
        totalCount={loading ? null : inventory.length}
      />

      {/* Gallery Grid - Always Full Width */}
      <div className="relative">
        {loading ? (
          <div className="p-8 text-center text-gray-600">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-500 border-t-transparent mx-auto mb-4"></div>
            Loading inventory...
          </div>
        ) : filteredInventory.length === 0 ? (
          <div className="p-8 text-center text-gray-600">
            {searchQuery
              ? 'No items found matching your search.'
              : selectedCategory
                ? `No items found in the selected category.`
                : 'No inventory items found.'
            }
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredInventory.map((item) => {
              const stockPercentage = item.initialStock > 0 ? (item.currentStock / item.initialStock) * 100 : 0;

              let gradientClass = '';
              let ringClass = '';
              let extraClasses = '';

              if (item.currentStock === 0) {
                // Special alerting for out of stock items
                gradientClass = 'bg-gradient-to-br from-red-100 to-red-200 border-red-400';
                ringClass = 'ring-4 ring-red-400';
              } else if (stockPercentage > 60) {
                gradientClass = 'bg-gradient-to-br from-green-50 to-green-100 border-green-200';
              } else if (stockPercentage >= 40) {
                gradientClass = 'bg-gradient-to-br from-yellow-50 to-orange-100 border-orange-200';
              } else {
                gradientClass = 'bg-gradient-to-br from-red-50 to-red-100 border-red-200';
              }

              return (
                <div
                  key={item._id}
                  onClick={() => setSelectedItemForDetail(item)}
                  className={`rounded-lg shadow-md overflow-hidden cursor-pointer hover:shadow-xl transition-shadow duration-200 border-2 ${gradientClass} ${ringClass} ${extraClasses} ${selectedItemForDetail ? 'pointer-events-none' : ''}`}
                >
                  {/* Image Section - Bigger image (60% height) */}
                  <div className="h-52 relative bg-white">
                    {item.photo ? (
                      <img
                        src={item.photo}
                        alt={item.modelName}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                  </div>

                  {/* Stock Display Section - Prominent (40% height) */}
                  <div className={`flex-1 flex flex-col justify-center items-center p-3 ${
                    item.currentStock === 0
                      ? 'bg-gradient-to-br from-red-100 to-red-200'
                      : stockPercentage > 60
                        ? 'bg-gradient-to-br from-green-50 to-green-100'
                        : stockPercentage >= 40
                          ? 'bg-gradient-to-br from-yellow-50 to-orange-100'
                          : 'bg-gradient-to-br from-red-50 to-red-100'
                  }`}>
                    {/* Stock Status Badge */}
                    <div className={`mb-2 px-3 py-1 rounded-full text-xs font-bold ${
                      item.currentStock === 0
                        ? 'bg-red-600 text-white'
                        : item.currentStock <= 5
                          ? 'bg-red-500 text-white'
                          : stockPercentage > 60
                            ? 'bg-green-600 text-white'
                            : stockPercentage >= 40
                              ? 'bg-orange-600 text-white'
                              : 'bg-red-600 text-white'
                    }`}>
                      {getStockStatusLabel(item.currentStock)}
                    </div>

                    {/* Large Stock Number */}
                    <div className={`text-5xl font-black mb-1 ${
                      item.currentStock === 0
                        ? 'text-red-600'
                        : item.currentStock <= 5
                          ? 'text-red-500'
                          : stockPercentage > 60
                            ? 'text-green-600'
                            : stockPercentage >= 40
                              ? 'text-orange-600'
                              : 'text-red-600'
                    }`}>
                      {item.currentStock}
                    </div>

                    {/* Stock Label */}
                    <div className="text-sm text-gray-600 font-medium mb-2">
                      LEFT
                    </div>

                    {/* Model Info */}
                    <div className="text-center">
                      <div className="text-sm font-bold text-gray-800 truncate">{item.modelName}</div>
                      <div className="text-base font-semibold text-gray-700">{formatModelCode(item.size, item.modelNumber)}</div>
                      {item.retailOnly && (
                        <span className="inline-flex mt-1 px-2 py-0.5 text-[10px] font-medium rounded-full bg-amber-100 text-amber-800">
                          Retail only
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Backdrop and Sliding Panel */}
        {selectedItemForDetail && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
              onClick={() => setSelectedItemForDetail(null)}
            />

            {/* Sliding Panel */}
            <div className="fixed top-0 right-0 h-full w-[36rem] max-w-[90vw] bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto">
              {/* Panel Header */}
              <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-start">
                <h2 className="text-2xl font-bold text-gray-800">Item Details</h2>
                <button
                  onClick={() => setSelectedItemForDetail(null)}
                  className="text-gray-500 hover:text-gray-700 text-xl p-1 hover:bg-gray-100 rounded"
                >
                  ✕
                </button>
              </div>

              {/* Panel Content */}
              <div className="p-6 space-y-6">
                {/* Large Image */}
                <div className="w-full">
                  <div className="aspect-square rounded-lg overflow-hidden max-w-sm mx-auto">
                    {selectedItemForDetail.photo ? (
                      <img
                        src={selectedItemForDetail.photo}
                        alt={selectedItemForDetail.modelName}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                        <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                  </div>
                </div>

                {/* Item Info */}
                <div className="space-y-4">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800">{selectedItemForDetail.modelName}</h3>
                    <p className="text-gray-600 text-lg">{formatModelCode(selectedItemForDetail.size, selectedItemForDetail.modelNumber)}</p>
                    <p className="text-sm text-gray-500 mt-1">Category: {selectedItemForDetail.size || 'N/A'}</p>
                    {selectedItemForDetail.retailOnly ? (
                      <span className="inline-flex mt-2 px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-800">
                        Retail only
                      </span>
                    ) : (
                      <span className="inline-flex mt-2 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                        Wholesale OK
                      </span>
                    )}
                  </div>

                  {/* Prices */}
                  <div className="grid grid-cols-1 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600">Retail Price</p>
                      <p className="text-2xl font-bold text-green-600">₹{roundRupees(selectedItemForDetail.retailPrice).toLocaleString('en-IN')}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600">Wholesale Price</p>
                      <p className="text-2xl font-bold text-blue-600">₹{roundRupees(selectedItemForDetail.wholesalePrice).toLocaleString('en-IN')}</p>
                    </div>
                  </div>

                  {/* Stock Information */}
                  <div>
                    <h4 className="font-semibold text-gray-800 text-lg mb-3">Stock Information</h4>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-gray-50 p-4 rounded-lg text-center">
                        <p className="text-sm text-gray-600">Initial</p>
                        <p className="text-2xl font-bold text-gray-800">{selectedItemForDetail.initialStock ?? 0}</p>
                      </div>
                      <div className="bg-green-50 p-4 rounded-lg text-center">
                        <p className="text-sm text-green-600">Sold</p>
                        <p className="text-2xl font-bold text-green-800">{selectedItemForDetail.sold}</p>
                      </div>
                      <div className={`p-4 rounded-lg text-center ${selectedItemForDetail.currentStock <= 5 ? 'bg-red-50' : 'bg-green-50'}`}>
                        <p className={`text-sm ${selectedItemForDetail.currentStock <= 5 ? 'text-red-600' : 'text-green-600'}`}>Current</p>
                        <p className={`text-2xl font-bold ${selectedItemForDetail.currentStock <= 5 ? 'text-red-800' : 'text-green-800'}`}>
                          {selectedItemForDetail.currentStock}
                        </p>
                      </div>
                    </div>
                    {selectedItemForDetail.initialStock > 0 && (
                      <p className="text-sm text-gray-600 mt-2 text-center">
                        {Math.round((selectedItemForDetail.currentStock / selectedItemForDetail.initialStock) * 100)}% remaining
                      </p>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="pt-4 border-t space-y-3">
                    <button
                      onClick={() => {
                        setSelectedItem(selectedItemForDetail);
                        setIsModalOpen(true);
                      }}
                      className="admin-btn admin-btn-neutral w-full py-3 px-6 text-lg font-semibold"
                    >
                      Edit Item
                    </button>
                    <button
                      onClick={() => handleDeleteClick(selectedItemForDetail)}
                      disabled={deleteLoading}
                      className="admin-btn admin-btn-danger w-full py-3 px-6 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Delete Item
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {showDeleteConfirm && itemToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Delete Inventory Item</h3>
              <p className="text-sm text-gray-500 mb-4">
                Are you sure you want to delete <strong>"{itemToDelete.modelName}"</strong> ({formatModelCode(itemToDelete.size, itemToDelete.modelNumber)})?
                This action cannot be undone.
              </p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={handleDeleteCancel}
                  disabled={deleteLoading}
                  className="admin-btn admin-btn-modal-cancel disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={deleteLoading}
                  className="admin-btn admin-btn-modal-confirm admin-btn-danger disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {deleteLoading && (
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                  )}
                  {deleteLoading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <InventoryModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSave}
        item={selectedItem}
        inventory={inventory}
      />
    </div>
  );
};

export default InventoryGalleryPage;
